
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/fun-ui/Dialog";
import { Button } from "@/components/fun-ui/Button";
import { Textarea } from "../ui/textarea";
import { HTMLAttributes, useEffect, useMemo, useRef, useState } from "react";
import { fun } from "@/lib/request";
import { toast } from "react-hot-toast";
import { api } from "@/lib/env";
import { FeedbackScene, type Feedback as FeedbackConfig } from "@/stores/slices/commonSlice";
import { useFeedback } from "@/stores/appStore";
import { cn, transferCamelToSnake } from "@/lib/utils";
import { Checkbox } from "../ui/checkbox";
import { AlertCircleIcon, WechatIcon } from "../fun-ui/icons";
import { Tip } from "../fun-ui/Tip";
import ReactMarkdown from "react-markdown";

export const Feedback = ({
  scene = 'default',
  children = '',
  sceneContents,
  autoOpenKey,
  onBeforeOpen,
}: {
  scene: FeedbackScene;
  children: string | React.JSX.Element;
  // 可以补充一些额外的提交参数
  sceneContents?: Record<string, any>;
  autoOpenKey?: string | number | null;
  onBeforeOpen?: () => boolean | Promise<boolean>;
}) => {
  const {
    value: feedbackConfig,
    fetch
  } = useFeedback();

  const defaultContent = useMemo(() => ({
    ...sceneContents,
    score: 0,
    suggestions: '',
    content: '',
    contact: false,
  }), [sceneContents]);

  const [content, setContent] = useState(defaultContent);
  const contentRef = useRef(defaultContent);
  const [open, setOpen] = useState(false);
  const lastAutoOpenKeyRef = useRef<string | number | null>(null);
  const [loading, setLoading] = useState(false);
  const config = useMemo(() => {
    return feedbackConfig?.scene[scene] || feedbackConfig?.scene['default'] || null;
  }, [feedbackConfig, scene]);
  const getContentKey = (configKey: string) => {
    return feedbackConfig?.[configKey]?.key || configKey;
  };

  const buttonDisabled = useMemo(() => {
    if (feedbackConfig && config?.length) {
      return !config.every((key) => {
        const itemConfig = feedbackConfig[key];
        const contentKey = itemConfig.key || key;
        const required = itemConfig.required;
        let able = !!(content as any)[contentKey];
        if (contentKey === 'suggestions') {
          able = content.score > 6 || !!content.suggestions.length;
        }
        return required ? able : true;
      });
    }
    return true;
  }, [content, config, feedbackConfig]);
  useEffect(() => {
    if (!open) {
      setContent(defaultContent);
    }
  }, [defaultContent, open]);

  useEffect(() => {
    contentRef.current = content;
  }, [content]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    if (!feedbackConfig || !config || autoOpenKey == null) {
      return;
    }
    if (lastAutoOpenKeyRef.current === autoOpenKey) {
      return;
    }
    lastAutoOpenKeyRef.current = autoOpenKey;
    setOpen(true);
  }, [autoOpenKey, config, feedbackConfig]);

  const handleOpenChange = async (nextOpen: boolean) => {
    if (!nextOpen) {
      setOpen(false);
      return;
    }
    if (onBeforeOpen) {
      const shouldOpen = await onBeforeOpen();
      if (!shouldOpen) {
        return;
      }
    }
    setOpen(true);
  };

  if (!feedbackConfig || !config) {
    return children;
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      void handleOpenChange(nextOpen);
    }}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="w-[480px]" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{typeof feedbackConfig.title === 'string' ? feedbackConfig.title : (feedbackConfig.title[scene] || feedbackConfig.title['default'])}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col">
          {
            config.map((key) => {
              const configItemKey = getContentKey(key);
              if (configItemKey === 'score') {
                const o = feedbackConfig[key]
                return (
                  <div key={key}>
                    <div className="text-sm flex flex-row items-center">
                      {
                        o.required ? <span className="text-destructive">*</span> : null
                      }
                      <ReactMarkdown components={{
                        a: ({ children, ...props }: HTMLAttributes<HTMLAnchorElement>) => (
                          <a
                            target="_blank"
                            className={cn("text-primary hover:underline underline-offset-4 cursor-pointer")}
                            {...props}
                          >{children}</a>
                        ),
                      }}>{o.label}</ReactMarkdown>
                    </div>
                    <div className="flex flex-row gap-2 mt-4">
                      {
                        (o.options as FeedbackConfig['score']['options']).map((option) => {
                          return (
                            <div
                              key={option.score}
                              className="flex-1 flex border rounded-md py-2.5 gap-2 leading-none border-border text-muted-foreground cursor-pointer items-center justify-center  select-none"
                              style={content.score === option.score ? {
                                borderColor: option.style.borderColor,
                                backgroundColor: option.style.backgroundColor,
                                color: option.style.color,
                              } : {}}
                              onClick={() => {
                                setContent({
                                  ...contentRef.current,
                                  suggestions: '',
                                  score: option.score,
                                });
                              }}
                            >
                              <span>{option.emoji}</span>
                              <span>{option.label}</span>
                            </div>
                          );
                        })
                      }
                    </div>
                  </div>
                )
              } else if (configItemKey === 'suggestions') {
                const o = feedbackConfig[key];
                const selectSuggestions = content.suggestions.split(',');
                const suggestions = (feedbackConfig.score.options.find((item) => {
                  return item.score === content.score;
                })?.suggestions || []);
                if (!suggestions.length) {
                  return null;
                }
                return (
                  <div key={key}>
                    <div className="mt-6 text-sm">{
                      o.required ? <span className="text-destructive">*</span> : null
                    }{o.label}</div>
                    <div className="flex flex-row flex-wrap gap-2 mt-4">
                      {
                        suggestions.map((suggestion) => {
                          return (
                            <div
                              key={suggestion}
                              className={cn(
                                "border border-border rounded-md py-1.5 px-3 text-muted-foreground cursor-pointer items-center justify-center text-xs select-none",
                                selectSuggestions.includes(suggestion) && "border-primary text-primary bg-primary/10"
                              )}
                              onClick={() => {
                                const ss = suggestions.filter((s) => {
                                  return suggestion === s ? !selectSuggestions.includes(s) : selectSuggestions.includes(s);
                                })
                                setContent({
                                  ...contentRef.current,
                                  suggestions: ss.join(','),
                                });
                              }}
                            >
                              {suggestion}
                            </div>
                          )
                        })
                      }
                    </div>
                  </div>
                )
              } else if (configItemKey === 'content') {
                const o = feedbackConfig[key];
                return (
                  <div
                    key={key}
                    className="flex-1 w-full mt-5"
                  >
                    <Textarea
                      maxLength={o.maxLength}
                      placeholder={o.placeholder}
                      className="outline-none ring-0 resize-none focus-visible:ring-0"
                      value={content.content}
                      onChange={(e) => {
                        setContent({
                          ...contentRef.current,
                          content: e.currentTarget.value,
                        });
                      }}
                    />
                  </div>
                );
              } else if (configItemKey === 'contact') {
                const o = feedbackConfig[key];
                return (
                  <label
                    key={key}
                    className="text-annotation-foreground flex flex-row items-center text-sm gap-2.5 mt-3.5 select-none cursor-pointer"
                  >
                    <Checkbox
                      checked={content.contact}
                      onCheckedChange={(value) => {
                        setContent({
                          ...contentRef.current,
                          contact: !!value,
                        })
                      }}
                      className="data-[state=unchecked]:border-annotation-foreground"
                    />
                    <div className="flex gap-1 items-center ">
                      <WechatIcon className="text-constructive" size={16} />
                      <span>{o.label}</span>
                      <Tip className="cursor-help" label={o.tip}><AlertCircleIcon size={12} /></Tip>
                    </div>
                  </label>
                )
              }
              return null;
            })
          }
        </div>
        <DialogFooter className="flex flex-row justify-end">
          <Button
            variant="outline"
            size="sm"
            className="px-3 min-w-[80px]"
            onClick={() => {
              setOpen(false);
            }}
          >
            取消
          </Button>
          <Button
            disabled={loading || buttonDisabled}
            size="sm"
            className="px-3 min-w-[120px]"
            onClick={async () => {
              try {
                setLoading(true);
                toast.loading('反馈提交中', {
                  id: 'sumbit'
                });
                const {
                  suggestions: suggestion,
                  ...rest
                } = content;
                const { data }: {
                  data: {
                    message: string;
                    code: number;
                    data: Object;
                  },
                } = await fun.post(api('/common/feedback', true), {
                  scene: transferCamelToSnake(scene),
                  suggestion,
                  ...rest,
                });
                toast.dismiss('sumbit');
                if (data.code === 0) {
                  setOpen(false);
                  toast.success('反馈已提交');
                } else {
                  toast.error(data.message);
                }
              } catch (error) {
              } finally {
                setLoading(false);
              }
            }}
          >
            提交
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
